/**
 * LeadsKanbanView
 * Quadro de leads por estágio (@dnd-kit): arrastar muda o estágio.
 */

import { useMemo, useState } from 'react';
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { Archive } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { LeadStage } from '../../types/leads';
import type { Lead } from '../../types/leads';
import { LEADS_CONFIG } from '../../config/leads.config';
import { useLeadStatusTransition } from '../../hooks/useLeadStatusTransition';
import { getErrorMessage } from '../../utils/error.utils';
import { LeadKanbanColumn } from './LeadKanbanColumn';
import { LeadKanbanCard } from './LeadKanbanCard';
import { ACTIVE_LEAD_STAGES, ARCHIVED_LEAD_STAGES } from './lead-format';

export interface LeadsKanbanViewProps {
  leads: Lead[];
  onStageChange: (leadId: string, newStage: LeadStage) => Promise<void>;
  onLeadClick: (lead: Lead) => void;
}

export function LeadsKanbanView({ leads, onStageChange, onLeadClick }: LeadsKanbanViewProps) {
  const [activeLead, setActiveLead] = useState<Lead | null>(null);
  const [showArchived, setShowArchived] = useState<boolean>(LEADS_CONFIG.SHOW_ARCHIVED_BY_DEFAULT);

  // Requer 8px de movimento para iniciar o arrastar (clique continua abrindo o lead)
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const { validateTransition, canDragLead } = useLeadStatusTransition();

  const archivedCount = useMemo(() => leads.filter((lead) => ARCHIVED_LEAD_STAGES.includes(lead.stage)).length, [leads]);

  const leadsByStage = useMemo(() => {
    const grouped = Object.fromEntries(Object.values(LeadStage).map((stage) => [stage, [] as Lead[]])) as Record<LeadStage, Lead[]>;
    leads.forEach((lead) => {
      // Estágio desconhecido cai em Contato Inicial
      (grouped[lead.stage] ?? grouped[LeadStage.CONTATO_INICIAL]).push(lead);
    });
    return grouped;
  }, [leads]);

  const handleDragStart = (event: DragStartEvent) => {
    const lead = leads.find((l) => l.id === event.active.id);
    if (lead && canDragLead(lead)) setActiveLead(lead);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveLead(null);
    if (!over) return;

    const leadId = active.id as string;
    const targetStage = over.id as LeadStage;
    const lead = leads.find((l) => l.id === leadId);
    if (!lead) {
      toast.error('Lead não encontrado');
      return;
    }

    const validation = validateTransition(lead, targetStage);
    if (!validation.allowed) {
      toast.error(validation.reason || 'Transição não permitida');
      return;
    }
    if (lead.stage === targetStage) return;

    try {
      await onStageChange(leadId, targetStage);
      toast.success('Lead movido com sucesso!');
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Erro ao mover lead'));
    }
  };

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="flex flex-col gap-3">
        <label className="flex w-fit cursor-pointer select-none items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="size-4 accent-primary"
          />
          Mostrar arquivados
          {!showArchived && archivedCount > 0 && LEADS_CONFIG.SHOW_ARCHIVED_COUNT_BADGE && (
            <span className="tabular-nums text-muted-foreground">({archivedCount})</span>
          )}
        </label>

        {!showArchived && archivedCount > 0 && LEADS_CONFIG.SHOW_ARCHIVED_INFO_BANNER && (
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border bg-muted/40 p-2 text-sm">
            <Archive className="size-4 text-muted-foreground" aria-hidden="true" />
            {archivedCount === 1
              ? '1 lead arquivado (convertido ou perdido) está oculto.'
              : `${archivedCount} leads arquivados (convertidos ou perdidos) estão ocultos.`}
            <Button variant="link" size="sm" className="h-auto p-0" onClick={() => setShowArchived(true)}>
              Mostrar arquivados
            </Button>
          </p>
        )}

        <div className="overflow-x-auto pb-2">
          <div className="flex min-w-max gap-3">
            {ACTIVE_LEAD_STAGES.map((stage) => (
              <LeadKanbanColumn key={stage} stage={stage} leads={leadsByStage[stage]} onLeadClick={onLeadClick} />
            ))}
            {showArchived &&
              ARCHIVED_LEAD_STAGES.map((stage) => (
                <LeadKanbanColumn key={stage} stage={stage} leads={leadsByStage[stage]} onLeadClick={onLeadClick} isArchived />
              ))}
          </div>
        </div>
      </div>

      <DragOverlay>
        {activeLead ? (
          <div className="rotate-2 opacity-90">
            <LeadKanbanCard lead={activeLead} onClick={() => { /* sobreposição do arrastar */ }} />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

export default LeadsKanbanView;
